import { z } from "zod";

import { DEFAULT_THEME_RADIUS } from "../canvas/radiusScale.ts";
import { ColorTokens, type ThemeVariant } from "../canvas/theme.ts";

/**
 * A theme, as one object.
 *
 * Five token groups plus two reserved keys. Light and dark live in the SAME
 * theme — `color` is per mode, not a second theme paired by name — because the
 * alternative is a `blue` / `blueDark` split where every palette is registered
 * twice, the pairing is a string operation, and the radius of the dark half has
 * to be inherited from the light one.
 *
 * Each group compiles to a Tailwind v4 namespace rather than to custom CSS, so
 * a theme change moves the shadcn components rendering in the same document as
 * well as the elements we emit ourselves. `compileTheme.ts` is that mapping.
 *
 * Groups the editor cannot reach yet — elevation, and the type scale — are
 * OPTIONAL, and the built-ins state none of them. An absent group emits
 * nothing, which leaves Tailwind's own ramp painting it, which is what paints
 * it today. The field exists so that wiring it up later is an edit rather than
 * a schema change and a migration of every stored theme.
 */

/** Which half of a theme a screen wears. */
export const ThemeMode = z.enum(["light", "dark"]);
export type ThemeMode = z.infer<typeof ThemeMode>;

/**
 * The attribute that selects the mode.
 *
 * A compiled theme carries both modes, light at `:root` and dark behind this
 * attribute, so swapping appearance is one `setAttribute` on the document
 * rather than a stylesheet rebuild — and one compiled string can be cached per
 * theme instead of per screen per appearance.
 */
export const THEME_APPEARANCE_ATTRIBUTE = "data-appearance";

/**
 * A token group that MAY vary by mode.
 *
 * Colour always does. Elevation usually should — a dark surface needs its own
 * shadow alpha rather than an inverted light one. Radius, spacing and
 * typography have no reason to, and are not modal.
 */
export type Modal<T> = T | { light: T; dark: T };

/**
 * Per-mode FIRST, and the order is the whole correctness of this.
 *
 * `z.union` takes the first member that PARSES, not the best fit, and a group
 * whose fields are all optional accepts anything — elevation states both
 * `shadow` and `blur` optionally, so a plain `ElevationTokens` reads
 * `{ light, dark }` by stripping both keys and succeeding at `{}`. Tried in
 * the other order that is not a parse error, it is a theme that loses both of
 * its modes on the way through the schema and says nothing.
 *
 * Nothing is lost the other way round: a group stated once has neither key, so
 * it fails the per-mode branch and falls through to itself.
 */
const modal = <T extends z.ZodType>(value: T) =>
  z.union([z.object({ light: value, dark: value }), value]);

// Safe as a discriminator because no group has a `light` or `dark` token of
// its own — a colour set or an elevation ramp with those keys would be read as
// a per-mode pair.
const isPerMode = <T>(value: Modal<T>): value is { light: T; dark: T } =>
  typeof value === "object" &&
  value !== null &&
  "light" in value &&
  "dark" in value;

/** One mode of a modal group. A group stated once resolves to itself. */
export const resolveModal = <T>(value: Modal<T>, mode: ThemeMode): T =>
  isPerMode(value) ? value[mode] : value;

/**
 * A font family, split into the name and what follows it.
 *
 * The name is separate because it is not only a CSS token: the Google Fonts
 * link that a screen's document loads is built from the families a theme names,
 * so a stack stored as one opaque string could not be turned into a URL.
 */
export const ThemeFontFamily = z.object({
  /** As Google Fonts names it — `Inter`, `Source Serif 4`. */
  name: z.string(),
  /** What follows the name in the CSS stack, written as CSS. */
  fallback: z.string(),
});
export type ThemeFontFamily = z.infer<typeof ThemeFontFamily>;

/** `Inter` + its fallbacks → the stack a `font-family` takes. */
export const fontFamilyStack = (family: ThemeFontFamily): string =>
  `"${family.name}", ${family.fallback}`;

/** The three families Tailwind gives a utility each. */
export const FONT_ROLES = ["sans", "serif", "mono"] as const;
export const FontRole = z.enum(FONT_ROLES);
export type FontRole = z.infer<typeof FontRole>;

export const FontRoles = z.object({
  /** The role body text takes when nothing names one. */
  default: FontRole,
  sans: ThemeFontFamily,
  serif: ThemeFontFamily,
  mono: ThemeFontFamily,
});
export type FontRoles = z.infer<typeof FontRoles>;

/** One `--text-*` step: a size, and the leading it implies. */
export const TypeStep = z.object({
  size: z.string(),
  /** A ratio or a length, as Tailwind's `--text-*--line-height` states it. */
  lineHeight: z.string().optional(),
});
export type TypeStep = z.infer<typeof TypeStep>;

/**
 * The `--text-*`, `--leading-*` and `--tracking-*` steps a theme overrides.
 *
 * Sparse on purpose: a theme states the steps it moves and Tailwind's own value
 * stands for the rest, so a theme that only wants a larger `text-sm` doesn't
 * have to restate the whole ramp to say it.
 */
export const TypeScale = z.object({
  size: z.record(z.string(), TypeStep).optional(),
  leading: z.record(z.string(), z.string()).optional(),
  tracking: z.record(z.string(), z.string()).optional(),
});
export type TypeScale = z.infer<typeof TypeScale>;

export const TypographyTokens = z.object({
  families: FontRoles,
  scale: TypeScale.optional(),
});
export type TypographyTokens = z.infer<typeof TypographyTokens>;

/**
 * The `--shadow-*` and `--blur-*` ramps, as CSS values.
 *
 * Sparse for the same reason {@link TypeScale} is.
 */
export const ElevationTokens = z.object({
  shadow: z.record(z.string(), z.string()).optional(),
  blur: z.record(z.string(), z.string()).optional(),
});
export type ElevationTokens = z.infer<typeof ElevationTokens>;

/**
 * The radius the whole `rounded-*` scale is a multiple of.
 *
 * The steps themselves are not stored — `radiusScale.ts` writes the ramp down
 * once, and both the stylesheet and the parser read it from there. A stored
 * copy of a derived scale is a copy that drifts.
 */
export const RadiusTokens = z.object({
  base: z.string().default(DEFAULT_THEME_RADIUS),
});
export type RadiusTokens = z.infer<typeof RadiusTokens>;

/**
 * Tailwind v4's spacing base. Every `p-4` and `gap-6` is a multiple of it.
 *
 * One number, because that is what the whole scale hangs off. Whether density
 * needs steps of its own is step 10's question.
 */
export const SpacingTokens = z.object({
  base: z.string(),
});
export type SpacingTokens = z.infer<typeof SpacingTokens>;

/** Brand role → org asset id. Reserved for step 5; nothing reads it yet. */
export const AssetSlots = z.record(z.string(), z.string());
export type AssetSlots = z.infer<typeof AssetSlots>;

export const THEME_INSTRUCTIONS_MAX_LENGTH = 10_000;

/** The templates that ship in code, in the order a picker shows them. */
export const BUILTIN_THEME_NAMES = [
  "default",
  "blue",
  "grayscale",
  "green",
  "ice",
  "orange",
  "red",
  "rose",
  "violet",
  "yellow",
] as const;
export type BuiltinThemeName = (typeof BUILTIN_THEME_NAMES)[number];
export type BuiltinThemeId = `builtin:${BuiltinThemeName}`;

const BUILTIN_ID_PREFIX = "builtin:";

export const builtinThemeId = (name: BuiltinThemeName): BuiltinThemeId =>
  `${BUILTIN_ID_PREFIX}${name}`;

/**
 * A {@link BuiltinThemeId}, or the uuid of a theme in an org's library.
 *
 * A built-in resolves with no database row, which is what lets a generation
 * pick one before themes are storable at all. Forking one copies it into the
 * org, where it gets a uuid like any other.
 */
export type ThemeId = string;

/** A theme, and the mode a screen wears it in. */
export type ResolvedTheme = { theme: Theme; mode: ThemeMode };

export const Theme = z.object({
  id: z.string(),
  name: z.string(),
  color: modal(ColorTokens),
  radius: RadiusTokens,
  spacing: SpacingTokens,
  typography: TypographyTokens,
  elevation: modal(ElevationTokens).optional(),
  /** Reserved — see {@link AssetSlots}. */
  assets: AssetSlots.optional(),
  /**
   * How to use the theme, in its author's words. Only a person writes it, in
   * the theme editor; screen builders read it next to the tokens. Versioned
   * with them, so an edit reaches other files only when it is published.
   */
  instructions: z.string().max(THEME_INSTRUCTIONS_MAX_LENGTH).optional(),
});
export type Theme = z.infer<typeof Theme>;

/**
 * The theme's name as a file — `builtin:blue` → `blue`.
 *
 * Code export writes one stylesheet per theme in a bundle and every screen
 * imports its own, so the id has to survive as a filename. The `builtin:`
 * prefix comes off because a generated project has no other kind of theme to
 * tell it apart from. Both modes share one file, since a compiled theme carries
 * both — which is why a bundle mixing a light and a dark screen of the same
 * theme now writes one stylesheet where it used to write two.
 */
export const themeStylesheetName = (theme: Theme): string =>
  theme.id.startsWith(BUILTIN_ID_PREFIX)
    ? theme.id.slice(BUILTIN_ID_PREFIX.length)
    : theme.id;

/** One mode's colours. */
export const themeColors = (theme: Theme, mode: ThemeMode): ColorTokens =>
  resolveModal(theme.color, mode);

/** One mode's elevation ramps, if the theme states any. */
export const themeElevation = (
  theme: Theme,
  mode: ThemeMode,
): ElevationTokens | undefined =>
  theme.elevation === undefined
    ? undefined
    : resolveModal(theme.elevation, mode);

/**
 * One mode of a theme, flattened to what the parser, the property panel and
 * the prompt take. The radius comes from the theme rather than the mode —
 * what the light/dark merge bought, since a dark palette used to carry none.
 */
export const themeVariant = (theme: Theme, mode: ThemeMode): ThemeVariant => ({
  ...themeColors(theme, mode),
  radius: theme.radius.base,
});
